import { config } from '../config'
import { getRequestConfig } from '../utils/auth'

export type DashboardPeriod = '24h' | '7d' | '30d' | '90d' | '1y'

export interface DashboardStat {
  name: string
  value: string
  change: string
  changeType: 'positive' | 'negative' | 'neutral'
}

export interface DashboardTransaction {
  id: string
  description: string
  amount: string
  date: string
  type: 'billing' | 'response'
}

export interface MonthlyBillingData {
  month: string
  sent: number
  opened: number
  replied: number
  converted: number
}

export interface DashboardData {
  stats: DashboardStat[]
  recentTransactions: DashboardTransaction[]
  dateRange?: {
    startDate: string
    endDate: string
  }
}

class DashboardService {
  private baseUrl = config.apiUrl

  async getDashboardStats(period: DashboardPeriod): Promise<DashboardData> {
    try {
      const response = await fetch(`${this.baseUrl}/dashboard/stats?period=${period}`, {
        ...getRequestConfig(),
      })

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard stats')
      }

      return await response.json()
    } catch (error) {
      console.error('Error fetching dashboard stats:', error)
      // Return fallback data for demo purposes
      return this.getFallbackDashboardData(period)
    }
  }

  async getMonthlyBillingData(period: DashboardPeriod): Promise<MonthlyBillingData[]> {
    try {
      const response = await fetch(`${this.baseUrl}/dashboard/monthly-billing?period=${period}`, {
        ...getRequestConfig(),
      })

      if (!response.ok) {
        throw new Error('Failed to fetch monthly billing data')
      }

      return await response.json()
    } catch (error) {
      console.error('Error fetching monthly billing data:', error)
      // Return fallback data for demo purposes
      return this.getFallbackMonthlyData()
    }
  }

  private getFallbackDashboardData(period: DashboardPeriod): DashboardData {
    const now = new Date()
    const startDate = new Date()

    switch (period) {
      case '24h':
        startDate.setHours(now.getHours() - 24)
        break
      case '7d':
        startDate.setDate(now.getDate() - 7)
        break
      case '30d':
        startDate.setDate(now.getDate() - 30)
        break
      case '90d':
        startDate.setDate(now.getDate() - 90)
        break
      case '1y':
        startDate.setFullYear(now.getFullYear() - 1)
        break
    }

    return {
      stats: [
        {
          name: 'Total Invoices',
          value: '24',
          change: '+12%',
          changeType: 'positive'
        },
        {
          name: 'Active Subscriptions',
          value: '1,247',
          change: '+8%',
          changeType: 'positive'
        },
        {
          name: 'Payment Rate',
          value: '18.5%',
          change: '+2.3%',
          changeType: 'positive'
        },
        {
          name: 'Collection Rate',
          value: '4.2%',
          change: '-0.5%',
          changeType: 'negative'
        }
      ],
      recentTransactions: [
        {
          id: '1',
          description: 'Q4 Billing Cycle',
          amount: '500 invoices sent',
          date: new Date(Date.now() - 86400000).toISOString(),
          type: 'billing'
        },
        {
          id: '2',
          description: 'Reply from John Smith',
          amount: 'Interested in demo',
          date: new Date(Date.now() - 172800000).toISOString(),
          type: 'response'
        },
        {
          id: '3',
          description: 'Holiday Billing Campaign',
          amount: '750 invoices sent',
          date: new Date(Date.now() - 259200000).toISOString(),
          type: 'billing'
        },
        {
          id: '4',
          description: 'Reply from Sarah Johnson',
          amount: 'Meeting scheduled',
          date: new Date(Date.now() - 345600000).toISOString(),
          type: 'response'
        },
        {
          id: '5',
          description: 'Follow-up Billing',
          amount: '300 reminders sent',
          date: new Date(Date.now() - 432000000).toISOString(),
          type: 'billing'
        }
      ],
      dateRange: {
        startDate: startDate.toISOString(),
        endDate: now.toISOString()
      }
    }
  }

  private getFallbackMonthlyData(): MonthlyBillingData[] {
    return [
      { month: 'Jan', sent: 2400, opened: 1560, replied: 468, converted: 124 },
      { month: 'Feb', sent: 2800, opened: 1820, replied: 546, converted: 145 },
      { month: 'Mar', sent: 3200, opened: 2080, replied: 624, converted: 166 },
      { month: 'Apr', sent: 2900, opened: 1885, replied: 565, converted: 150 },
      { month: 'May', sent: 3500, opened: 2275, replied: 682, converted: 181 },
      { month: 'Jun', sent: 3800, opened: 2470, replied: 741, converted: 197 }
    ]
  }
}

export const dashboardService = new DashboardService()
export { DashboardService } 